import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import QS from 'qs'
import I18n from 'translations/i18n'
import { Button, Spacer, Text } from '@jisr-hr/design-system'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { addMinutes } from 'date-fns'
import { handleResponseErr } from 'utils/apiHelperUtils'
import { ReactComponent as OfficeIcon } from 'assets/images/office-icon.svg'
import { Modal } from 'components/global/atoms'
import { labels } from 'utils/whitelabel'
import { ORGANISATION_NAME, isDevelopmentStage, API_ROOT } from '../../../env'
import Slug from './Slug'
import LoginForm from './Form'
import VerificationForm from '../components/VerificationForm'
import LoginTemplate from '../components/loginTemplate'

function Login({ company, login_attempt, fetching, ...props }) {
  const { login_attempt_applicable, is_google_captcha_enabled, attempts } = login_attempt ?? {}
  const [state, setState] = useState({
    error: '',
    showError: false,
    showSlug: false,
    show2FAVerificationModal: false,
  })
  const [timerMinutes, setTimerMinutes] = useState(null)
  const [shouldChallangeCaptcha, setshouldChallangeCaptcha] = useState(false)
  const [isCaptchaRendered, setIsCaptchaRendered] = useState(false)
  const [loginValues, setLoginValues] = useState({})
  const history = useHistory()
  let timer = null

  const { search } = useLocation()
  const { from } = QS.parse(search, {
    ignoreQueryPrefix: true,
  })

  const replaceSubdomain = (newSubdomain) => {
    let url = window.location.hostname
    url = url.split('.')
    if (isDevelopmentStage && !localStorage.getItem('slug')) {
      setState((prev) => ({ ...prev, showSlug: false }))
      return localStorage.setItem('slug', newSubdomain)
    }
    url[0] = newSubdomain
    window.location.hostname = url.join('.')
    return true
  }

  const replaceSubdomainKsa = (newSubdomain) => {
    let url = window.location.hostname
    url = url.split('.')
    url[0] = newSubdomain
    if (!url.includes('ksa')) {
      window.location = `//${newSubdomain}.jisr.net.sa`
    }
  }

  const handleReject = (res, company_name) => {
    const newState = {
      error: handleResponseErr(res),
      showError: true,
    }
    if (company_name === 'signin' && !state.error) {
      newState.showError = false
    } else newState.showError = true
    timer = setTimeout(() => setState((prev) => ({ ...prev, ...newState })), 100)
    timer = setTimeout(() => setState((prev) => ({ ...prev, showSlug: true })), 100)
  }

  const handleValidateCompany = (company_name) => {
    if (company_name !== 'signin') {
      props
        .validateCompany({ company_name })
        .then(() => replaceSubdomain(company_name))
        .catch(({ response }) => {
          if (labels.name !== 'Jisr') {
            // no need to hit ksa api in whitelabels branches[projects]
            return handleReject({ response }, company_name)
          }

          if (response && response.status === 403) {
            return props
              .validateCompanyKsa({ company_name })
              .then(() => replaceSubdomainKsa(company_name))
              .catch((res) => handleReject(res, company_name))
          }

          return false // irrational eslint
        })
    } else {
      timer = setTimeout(() => setState((prev) => ({ ...prev, showSlug: true })), 100)
    }
  }

  const handleLoginFrameResponse = (e) => {
    if (e.data.source === 'loginFrame' && e.data.payload && e.data.payload.success) {
      localStorage.setItem('LoggedIn', true)
      if (e.data.payload?.data?.is_cookies === false) {
        props.frameSuccessRelogin({ access_token: e.data.payload?.data?.access_token })
      } else {
        props.frameSuccessLogin({ data: e.data.payload })
      }
    }
  }

  useEffect(() => {
    handleValidateCompany(ORGANISATION_NAME)
    localStorage.removeItem('suspended_token')
    localStorage.removeItem('LoggedIn')
    localStorage.removeItem('slug')
    window.addEventListener('message', handleLoginFrameResponse)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('message', handleLoginFrameResponse)
    }
  }, [])

  useEffect(() => {
    const passwordAgeToken = localStorage.getItem('password-age-token')
    if (passwordAgeToken) {
      history.replace(`/change_password?${passwordAgeToken}`)
    }
  }, [])

  useEffect(() => {
    const shouldChallange = login_attempt_applicable && is_google_captcha_enabled
    setshouldChallangeCaptcha(shouldChallange)
    setIsCaptchaRendered(false)
  }, [login_attempt_applicable, is_google_captcha_enabled, attempts])

  const handleOfficeBtn = () => {
    window.open(
      `${API_ROOT}/auth?slug=${ORGANISATION_NAME}&type=web&locale=en`,
      'jisrLogin',
      'width=560,height=340,toolbar=0,menubar=0,location=0',
    )
  }

  let windowObjectReference = null

  const handleSSOLogin = () => {
    if (windowObjectReference === null || windowObjectReference.closed) {
      windowObjectReference = window.open(
        `${API_ROOT}/saml/init?type=web&&slug=${company?.slug}`,
        undefined,
        'width=560,height=340,toolbar=1,menubar=0,location=0',
      )
    } else {
      windowObjectReference.focus()
    }
  }

  const loginFlow = (values, captchaToken) => {
    setIsCaptchaRendered(false)
    props
      .login({ type: 'Web', ...values, captcha_token: captchaToken })
      .then(({ value }) => {
        const { pathname } = props.location.state ? props.location.state.from : { pathname: '/' }
        if (value.data.data.suspended_token) {
          localStorage.setItem('suspended_token', value.data.data.suspended_token)
          return history.replace('/billing_details')
        }
        if (value.data.data.password_reset_token) {
          return history.replace(`/change_password?${value.data.data.password_reset_token}`)
        }
        if (value.data.data.two_factor_required) {
          setTimerMinutes(addMinutes(new Date(), 5))
          setState((prev) => ({
            ...prev,
            show2FAVerificationModal: true,
            loginCredentials: values,
          }))
          return true
        }
        if (from === 'foodics') {
          return history.replace('/setting/integration?from=foodics')
        }

        return history.replace(pathname)
      })
      .catch((e) => {
        if (e?.response?.status === 409) {
          setLoginValues(values)
          setshouldChallangeCaptcha(true)
          setIsCaptchaRendered(true)
        }
      })
  }
  const handleSubmit = (values) => {
    if (shouldChallangeCaptcha) {
      setIsCaptchaRendered(true)
      setLoginValues(values)
      return
    }
    loginFlow(values)
  }

  return (
    <LoginTemplate
      company={company}
      hideHeader={state.showSlug}
    >
      {state.showSlug ? (
        <Slug
          error={state.error}
          showError={state.showError}
          fetching={fetching}
          onSubmit={handleValidateCompany}
        />
      ) : (
        <div>
          <LoginForm
            onSubmit={handleSubmit}
            fetching={fetching}
            history={history}
            showCaptcha={isCaptchaRendered}
            onCaptchaChange={(captchaToken) => loginFlow(loginValues, captchaToken)}
          />
          <Spacer height="20px" />
          <div className="optional--login-box">
            <hr />
            <span>{I18n.t('OR')}</span>
          </div>
          <Spacer height="20px" />
          {labels.name !== 'Maken' && (
            <>
              <Button
                onClick={handleSSOLogin}
                className="blocked--centred-btn"
                size="large"
                variant="secondary"
                label={I18n.t('sso_login')}
                style={{
                  border: '1px solid var(--color-brand-primary-default-new)',
                  color: 'var(--color-brand-primary-default-new)',
                }}
              />
              <Spacer height="20px" />{' '}
            </>
          )}
          <Button
            onClick={handleOfficeBtn}
            className="blocked--centred-btn"
            size="large"
            variant="primary"
            startIcon={<OfficeIcon />}
            label={I18n.t('login_with_office365')}
            style={{ background: '#ea6446' }}
          />
          <Spacer height="16px" />
          {shouldChallangeCaptcha &&
            (attempts <= 0 ? (
              <Text
                typography="paragraph16"
                style={{ textAlign: 'center' }}
                color="var(--danger)"
              >
                {I18n.t('exceeded_tallowed_attempts')}
                <span style={{ marginInlineStart: 6 }}>
                  <Link to="/forgot_password">{I18n.t('change_password')}</Link>
                </span>
              </Text>
            ) : null)}
        </div>
      )}

      <Modal
        title={I18n.t('jisr_login_authentication', {
          name: labels.translation_name,
        })}
        open={state.show2FAVerificationModal}
        handleClose={() => {}}
        fetching={fetching}
        size="sm"
        noPadding
        hideClose
      >
        <VerificationForm
          initialValues={state.loginCredentials}
          timerMinutes={timerMinutes}
          onSubmit={handleSubmit}
        />
      </Modal>
    </LoginTemplate>
  )
}

Login.propTypes = {
  frameSuccessLogin: PropTypes.func.isRequired,
  frameSuccessRelogin: PropTypes.func.isRequired,
  validateCompany: PropTypes.func.isRequired,
  validateCompanyKsa: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  fetching: PropTypes.bool,
  location: PropTypes.shape(),
  company: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string]),
  login_attempt: PropTypes.shape(),
}

export default Login
